import { useEffect, useRef } from 'react';
import { Marker, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import type { Box } from '../../types';
import { markerColor } from '../../lib/boxes';

export function markerIcon(type: string) {
  return L.divIcon({
    className: 'box-marker-wrapper',
    html: `<span class="box-marker" style="background:${markerColor(type)}"></span>`,
    iconSize: [20, 20],
    iconAnchor: [10, 10]
  });
}

export const meIcon = () =>
  L.divIcon({ className: 'me-marker-wrapper', html: '<span class="me-marker"></span>', iconSize: [18, 18], iconAnchor: [9, 9] });

export const addressIcon = () =>
  L.divIcon({
    className: 'address-marker-wrapper',
    html: '<span class="address-marker"></span>',
    iconSize: [26, 36],
    iconAnchor: [13, 36]
  });

export function Recenter({ center }: { center: [number, number] | null }) {
  const map = useMap();
  useEffect(() => {
    if (!center) return;
    map.setView(center, Math.max(map.getZoom(), 16), { animate: true });
  }, [center, map]);
  return null;
}

interface MapClickHandlerProps {
  active: boolean;
  movingMode: boolean;
  onMapClick: (latlng: L.LatLng) => void;
}

export function MapClickHandler({ active, movingMode, onMapClick }: MapClickHandlerProps) {
  const map = useMapEvents({
    click(e) {
      if (!active) return;
      onMapClick(e.latlng);
    }
  });
  useEffect(() => {
    const container = map.getContainer();
    if (active) container.style.cursor = 'crosshair';
    else if (movingMode) container.style.cursor = 'grab';
    else container.style.cursor = '';
    return () => {
      container.style.cursor = '';
    };
  }, [active, movingMode, map]);
  return null;
}

interface DraggableMarkerProps {
  box: Box;
  movingMode: boolean;
  onDragEnd: (latlng: L.LatLng) => void;
  onClick: () => void;
}

export function DraggableMarker({ box, movingMode, onDragEnd, onClick }: DraggableMarkerProps) {
  const markerRef = useRef<L.Marker>(null);

  useEffect(() => {
    const marker = markerRef.current;
    if (!marker) return;
    if (movingMode) marker.dragging?.enable();
    else marker.dragging?.disable();
  }, [movingMode]);

  return (
    <Marker
      ref={markerRef}
      position={[box.latitude, box.longitude]}
      icon={markerIcon(box.type)}
      draggable={movingMode}
      eventHandlers={{
        click: onClick,
        dragend(e) {
          onDragEnd((e.target as L.Marker).getLatLng());
        }
      }}
    />
  );
}
